import type { TokenStore } from "@/domains/tokens/token-store"
import {
  priceToTick as coreP2T,
  tickToPriceString as coreTickToPriceString,
} from "@/domains/manual-cl-core"

/**
 * Convert a human-readable price string to a tick aligned to `tickSpacing`.
 * Delegates to manual-cl-core so the round-trip with `tickToPriceString` is
 * bit-exact against Uniswap SDK's reference — the legacy implementation was
 * asymmetric (priceToTick ignored decimals while tickToPrice applied
 * `× 10^(dec0-dec1)`) which produced min/max display values off by exactly
 * 10^(dec0-dec1) for any non-1:1-decimal pair.
 */
export function tryParseTickFromPrice(
  price: string | undefined,
  token0: TokenStore,
  token1: TokenStore,
  tickSpacing: number,
  invert: boolean = false,
): number | undefined {
  if (!price || price.trim() === "" || price === "-" || price === ".") {
    return undefined
  }

  const parsed = parseFloat(price)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return undefined
  }

  try {
    const alignedTick = coreP2T({ price, token0, token1, tickSpacing, invert })
    return selectClosestTickToPrice(parsed, alignedTick, token0, token1, tickSpacing, invert)
  } catch (error) {
    console.error("[tryParseTickFromPrice] Error parsing price:", error)
    return undefined
  }
}

function selectClosestTickToPrice(
  targetPrice: number,
  alignedTick: number,
  token0: TokenStore,
  token1: TokenStore,
  tickSpacing: number,
  invert: boolean,
): number {
  const candidates = [alignedTick - tickSpacing, alignedTick, alignedTick + tickSpacing]
  let bestTick = alignedTick
  let bestDistance = Number.POSITIVE_INFINITY
  for (const candidate of candidates) {
    let priceAtTick: number
    try {
      priceAtTick = Number(coreTickToPriceString({ tick: candidate, token0, token1, decimalPlaces: 18, invert }))
    } catch {
      continue
    }
    if (!Number.isFinite(priceAtTick) || priceAtTick <= 0) continue
    const distance = Math.abs(priceAtTick - targetPrice) / targetPrice
    if (distance < bestDistance) {
      bestDistance = distance
      bestTick = candidate
    }
  }
  return bestTick
}

/**
 * Convert a tick to a human-readable price string. 6-decimal default mirrors
 * the legacy behavior; bit-exact via the SDK.
 */
export function tickToPriceString(
  tick: number,
  token0: TokenStore,
  token1: TokenStore,
  decimalPlaces: number = 6,
  invert: boolean = false,
): string {
  try {
    return coreTickToPriceString({ tick, token0, token1, decimalPlaces, invert })
  } catch (error) {
    console.error("[tickToPriceString] Error converting tick to price:", error)
    return "0"
  }
}

/**
 * Calculate percentage difference between a price and current price
 * Returns the percentage deviation: ((price - currentPrice) / currentPrice) * 100
 */
export function calculatePriceDifference(
  price: number | undefined,
  currentPrice: number | undefined,
): number | undefined {
  if (price === undefined || currentPrice === undefined || currentPrice === 0) {
    return undefined
  }

  try {
    return ((price - currentPrice) / currentPrice) * 100
  } catch (error) {
    console.error("[calculatePriceDifference] Error calculating difference:", error)
    return undefined
  }
}

/**
 * Format price difference as a string with +/- prefix
 * e.g., "-0.07%" or "+0.05%"
 */
export function formatPriceDifference(difference: number | undefined): string | undefined {
  if (difference === undefined || isNaN(difference)) {
    return undefined
  }

  const sign = difference >= 0 ? "+" : ""
  return `${sign}${difference.toFixed(2)}%`
}

/**
 * Convert a percentage to a price based on current price
 * Formula: currentPrice * (1 + percentage / 100)
 */
export function getPriceFromPercentage(
  percentage: number | undefined,
  currentPrice: number | undefined,
): number | undefined {
  if (percentage === undefined || currentPrice === undefined || currentPrice === 0) {
    return undefined
  }

  try {
    return currentPrice * (1 + percentage / 100)
  } catch (error) {
    console.error("[getPriceFromPercentage] Error converting percentage to price:", error)
    return undefined
  }
}

/**
 * Round a tick to the nearest valid tick spacing
 */
export function roundToTickSpacing(tick: number, tickSpacing: number): number {
  return Math.round(tick / tickSpacing) * tickSpacing
}
